import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Utensils, GlassWater, Users, Calendar, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
const Particle = ({
  color
}: {
  color: string;
}) => {
  const randomX = Math.random() * 400 - 200;
  const randomY = Math.random() * 400 - 200;
  const randomScale = Math.random() * 2 + 0.5;
  return <motion.div initial={{
    opacity: 1,
    scale: 0,
    x: 0,
    y: 0
  }} animate={{
    opacity: 0,
    scale: randomScale,
    x: randomX,
    y: randomY
  }} transition={{
    duration: 0.8,
    ease: 'easeOut'
  }} className="absolute w-4 h-4 rounded-full z-[100] pointer-events-none" style={{
    backgroundColor: color
  }} data-id="element-361" />;
};
const experiences = [{
  id: 'nightlife',
  title: 'Nightlife',
  subtitle: 'Elite Entertainment',
  description: 'Weekly schedule, VIP table booking, and world-class entertainers.',
  icon: Sparkles,
  image: 'https://cdn.magicpatterns.com/uploads/ad6jT6urC8DptPHknEKxeE/SnapInsta.to_686901633_17925323268315122_4386797257290448497_n.jpg',
  href: '/nightlife',
  glowColor: 'rgba(147, 51, 234, 0.5)' // Purple
}, {
  id: 'brunch',
  title: 'Brunch',
  subtitle: 'Melodies & Mimosas',
  description: 'Karaoke brunch, bottomless food & mimosas, party atmosphere.',
  icon: Utensils,
  image: 'https://cdn.magicpatterns.com/uploads/mNTFqmsMdHbfPctSPfK7sR/HOF_18.jpg',
  href: '/brunch',
  glowColor: 'rgba(249, 115, 22, 0.5)' // Orange
}, {
  id: 'dining',
  title: 'Dining',
  subtitle: 'Gourmet & Social',
  description: 'Premium dining, happy hour specials, and bold flavors.',
  icon: GlassWater,
  image: 'https://cdn.magicpatterns.com/uploads/s3K8wKdfybMP1zxoh7PS3W/SnapInsta.to_684049710_18095297510328170_549094879827346722_n.jpg',
  href: '/dining',
  glowColor: 'rgba(59, 130, 246, 0.5)' // Blue
}, {
  id: 'private-events',
  title: 'Private Events Bookings',
  subtitle: 'Bespoke Affairs',
  description: 'Venue rental, corporate mixers, and custom event hosting.',
  icon: Users,
  image: 'https://cdn.magicpatterns.com/uploads/eSNnVE12Pb6djuzdqREZxv/SnapInsta.to_684815228_18095302001328170_2447475437854116174_n.jpg',
  href: '/corporate-events',
  glowColor: 'rgba(221, 162, 71, 0.5)' // Gold
}, {
  id: 'upcoming-events',
  title: 'Weekly Events',
  subtitle: "What's On",
  description: 'One-time specials, Africa Day Brunch, and seasonal events.',
  icon: Calendar,
  image: 'https://cdn.magicpatterns.com/uploads/va8WBqwRFPJVrp1Tyd8QNn/SnapInsta.to_684140561_18095297540328170_1227165002793297535_n.jpg',
  href: '/events',
  glowColor: 'rgba(239, 68, 68, 0.5)' // Red
}, {
  id: 'reservations',
  title: 'Reservations',
  subtitle: 'Book Your Spot',
  description: 'Context-aware booking for brunch, nightlife, and more.',
  icon: MessageSquare,
  image: 'https://cdn.magicpatterns.com/uploads/hCM9V9Vkj7KdrVKoueBuWs/HOF_12.jpg',
  href: '/book-now',
  glowColor: 'rgba(34, 197, 94, 0.5)' // Green
}];
export function ExperienceSelector() {
  const navigate = useNavigate();
  const [explodingId, setExplodingId] = useState<string | null>(null);
  const handleCardClick = (id: string, href: string) => {
    setExplodingId(id);
    // Wait for animation before navigating
    setTimeout(() => {
      navigate(href);
      setExplodingId(null);
    }, 600);
  };
  return <section className="pb-12 md:pb-16 bg-[#0B1014] relative z-30 -mt-4 md:-mt-8" data-id="element-362">
      <div className="container mx-auto px-3 md:px-6" data-id="element-363">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4" data-id="element-364">
          {experiences.map((exp, index) => <motion.div key={exp.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.5
        }} whileHover={{
          y: -5,
          transition: {
            duration: 0.2
          }
        }} className="relative" data-id="element-365">
              <button onClick={() => handleCardClick(exp.id, exp.href)} className="group relative block w-full h-[100px] md:h-[180px] overflow-hidden rounded-xl md:rounded-2xl border border-white/10 bg-[#11161A] shadow-xl transition-all duration-500 text-center md:text-left" data-id="element-366">
                {/* Explosion Particles */}
                <AnimatePresence data-id="element-367">
                  {explodingId === exp.id && <div className="absolute inset-0 flex items-center justify-center overflow-visible" data-id="element-368">
                      {[...Array(20)].map((_, i) => <Particle key={i} color={exp.glowColor.replace('0.5', '1')} data-id="element-369" />)}
                    </div>}
                </AnimatePresence>

                {/* Glowing Effect on Hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0 pointer-events-none" style={{
              boxShadow: `inset 0 0 40px ${exp.glowColor}`
            }} data-id="element-370" />

                {/* Background Image */}
                <img src={exp.image} alt={exp.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-40 group-hover:opacity-60" data-id="element-371" />

                {/* Overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1014] via-[#0B1014]/40 to-transparent z-10" data-id="element-372" />

                {/* Content */}
                <div className="absolute inset-0 z-20 p-2 md:p-4 flex flex-col items-center md:items-start justify-center md:justify-end" data-id="element-373">
                  <div className="mb-1 md:mb-2 inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-[#DDA247]/10 text-[#DDA247] group-hover:bg-[#DDA247] group-hover:text-[#0B1014] transition-all duration-300" data-id="element-374">
                    <exp.icon size={14} className="md:w-[16px] md:h-[16px]" data-id="element-375" />
                  </div>
                  <h3 className="text-[9px] md:text-base font-black text-white mb-0.5 uppercase tracking-tight leading-tight" data-id="element-376">
                    {exp.title}
                  </h3>
                  <p className="hidden md:block text-[#DDA247] text-[8px] font-black uppercase tracking-[0.1em] mb-1" data-id="element-377">
                    {exp.subtitle}
                  </p>
                  <p className="hidden md:block text-gray-400 text-[10px] leading-tight transition-all duration-500 line-clamp-2 opacity-0 group-hover:opacity-100" data-id="element-378">
                    {exp.description}
                  </p>
                </div>

                {/* Animated Border */}
                <div className="absolute inset-0 border border-white/5 rounded-xl md:rounded-2xl group-hover:border-[#DDA247]/30 transition-colors duration-500 z-30 pointer-events-none" data-id="element-379" />
              </button>
            </motion.div>)}
        </div>
      </div>
    </section>;
}